<template>
  <model-viewer
    ref="mv"
    :src="url"
    :alt="title"
    :poster="posterUrl"
    preload
    background-color="#ffffff"
    shadow-intensity="0"
    camera-controls
    interaction-prompt="none"
    autoplay
    :auto-rotate="autoRotate"
    ar
    ar-modes="scene-viewer quick-look webxr"
    max-camera-orbit="auto 359deg auto"
    min-camera-orbit="auto 0deg auto"
    environment-image="neutral"
    @load="isLoaded"
    @play="isPlaying"
    @pause="isPaused"
  >
    <template v-for="(hotspot, i) in hotspots">
      <v-tooltip
        :key="i"
        v-model="visible[i]"
        open-delay="300"
        open-on-click
        top
        color="#0f505f"
      >
        <template v-slot:activator="{ on }">
          <v-btn
            :key="`btn-${i}`"
            :slot="`hotspot-${hotspot.id}`"
            :data-position="hotspot.cameraTarget"
            :data-normal="hotspot.normal"
            fab
            dark
            x-small
            color="#0f505f9f"
            @click="hotspotClick(i)"
            v-on="on"
          >
            {{ i+1 }}
          </v-btn>
        </template>
        <span>{{ hotspot.title }}</span>
      </v-tooltip>
    </template>
    <slot />
    <v-btn
      id="ar-button"
      slot="ar-button"
    />
    <img
      src="../assets/img/mais-logo.png"
      :class="isMobileDevice ? 'logo d-none' : 'logo d-none d-sm-flex'"
      :style="desktopMenu ? 'left: 24px' : 'right: 24px'"
    >
    <snackbar :message="snackbarErrorMessage" />
  </model-viewer>
</template>

<script>
import '@google/model-viewer';
import { isMobile } from 'mobile-device-detect';
import Snackbar from '@/components/desktop/Snackbar';

export default {
  name: 'Viewer',
  components: {
    Snackbar,
  },
  props: {
    title: {
      type: String,
      default: '',
    },
    url: {
      type: String,
      default: '',
    },
    posterUrl: {
      type: String,
      default: '',
    },
    cameraTarget: {
      type: String,
      default: 'auto auto auto',
    },
    cameraOrbit: {
      type: String,
      default: 'auto auto auto',
    },
    fieldOfView: {
      type: String,
      default: 'auto',
    },
    activateAr: {
      type: Boolean,
    },
  },
  data: () => ({
    show: 0,
    visible: [],
    hotspots: [],
    tapDistance: 2,
    panning: false,
    panX: 0,
    panY: 0,
    startX: 0,
    startY: 0,
    lastX: 0,
    lastY: 0,
    metersPerPixel: 0,
    snackbarErrorMessage: '',
  }),
  computed: {
    desktopMenu() {
      return this.$store.state.UIParametersObj.desktopmenu === 'right';
    },
    isMobileDevice() {
      return isMobile;
    },
    autoRotate() {
      return this.$store.state.UIParametersObj.autorotate;
    },
  },
  watch: {
    '$store.state.snackbarErrorMessage': function (error) {
      this.snackbarErrorMessage = error;
    },
    show(value) {
      // Hide all annotations
      this.visible.fill(false, 0, this.hotspots.length);
      this.translateCamera(this.hotspots[value]);
      setTimeout(() => {
        this.visible = this.visible.map((v, i) => (i == value));
      }, 300);
    },
    url() {
      this.resetCamera();
    },
    cameraTarget() {
      this.resetCamera();
    },
    cameraOrbit() {
      this.resetCamera();
    },
    fieldOfView() {
      this.resetCamera();
    },
    activateAr() {
      const vm = this;
      vm.$refs.mv.activateAR();
    },
  },
  created() {
    this.visible.fill(false, 0, this.hotspots.length);
  },
  mounted() {
    this.setupPanning();
  },
  methods: {
    isLoaded() {
      this.$emit('model-loaded');
      // Attempt to bypass race condition and display the model correctly in MacOS Safari
      this.$refs.mv.environmentImage = '';

      // This reports the number of animations available in the model, if any
      this.$emit('animation-loaded', this.$refs.mv?.availableAnimations?.length);
    },
    isPlaying() {
      this.$emit('animation-paused', false);
    },
    isPaused() {
      this.$emit('animation-paused', true);
    },
    play() {
      this.$refs.mv.play();
    },
    pause() {
      this.$refs.mv.pause();
    },
    setHotspots(newHotspots) {
      this.hotspots = this.visible = [];
      this.$nextTick(() => {
        this.hotspots = newHotspots;
        this.visible.fill(false, 0, this.hotspots.length);
      });
    },
    hotspotClick(index) {
      if (this.show == index) {
        this.translateCamera(this.hotspots[index]);
      } else {
        this.show = index;
      }
    },
    translateCamera(hotspot) {
      const vm = this;
      if (hotspot) {
        vm.$refs.mv.cameraOrbit = hotspot.cameraOrbit;
        vm.$refs.mv.cameraTarget = hotspot.cameraTarget;
      }
    },
    resetCamera() {
      const { mv } = this.$refs;
      mv.cameraTarget = this.cameraTarget;
      mv.cameraOrbit = this.cameraOrbit;
      mv.fieldOfView = this.fieldOfView;
    },
    setupPanning() {
      const modelViewer = this.$refs.mv;
      // This prevents the context menu from opening when panning (right mouse button)
      modelViewer.addEventListener('contextmenu', (event) => {
        event.preventDefault();
        return false;
      });
      modelViewer.addEventListener('mousedown', this.onMouseDown, true);
      modelViewer.addEventListener('touchstart', this.onTouchStart, true);
      modelViewer.addEventListener('mousemove', this.onMouseMove, true);
      modelViewer.addEventListener('touchmove', this.onTouchMove, true);
      self.addEventListener('mouseup', this.onMouseUp, true);
      self.addEventListener('touchend', this.onTouchEnd, true);
    },
    startPan() {
      const modelViewer = this.$refs.mv;
      const orbit = modelViewer.getCameraOrbit();
      const { theta, phi, radius } = orbit;
      const psi = theta - modelViewer.turntableRotation;
      this.metersPerPixel = 0.75 * radius / modelViewer.getBoundingClientRect().height;
      this.panX = [-Math.cos(psi), 0, Math.sin(psi)];
      this.panY = [
        -Math.cos(phi) * Math.sin(psi),
        Math.sin(phi),
        -Math.cos(phi) * Math.cos(psi),
      ];
      modelViewer.interactionPrompt = 'none';
    },
    movePan(thisX, thisY) {
      const modelViewer = this.$refs.mv;
      const dx = (thisX - this.lastX) * this.metersPerPixel;
      const dy = (thisY - this.lastY) * this.metersPerPixel;
      this.lastX = thisX;
      this.lastY = thisY;

      const target = modelViewer.getCameraTarget();
      target.x += dx * this.panX[0] + dy * this.panY[0];
      target.y += dx * this.panX[1] + dy * this.panY[1];
      target.z += dx * this.panX[2] + dy * this.panY[2];
      modelViewer.cameraTarget = `${target.x}m ${target.y}m ${target.z}m`;

      // This pauses turntable rotation
      modelViewer.dispatchEvent(new CustomEvent(
        'camera-change', { detail: { source: 'user-interaction' } },
      ));
    },
    recenter(pointer) {
      const modelViewer = this.$refs.mv;
      this.panning = false;
      if (Math.abs(pointer.clientX - this.startX) > this.tapDistance
          || Math.abs(pointer.clientY - this.startY) > this.tapDistance) return;
      const rect = modelViewer.getBoundingClientRect();
      const x = pointer.clientX - rect.left;
      const y = pointer.clientY - rect.top;
      const hit = modelViewer.positionAndNormalFromPoint(x, y);
      modelViewer.cameraTarget = hit == null ? 'auto auto auto' : hit.position.toString();
    },
    onPointerUp(event) {
      const pointer = event.clientX ? event : event.changedTouches[0];
      if (Math.abs(pointer.clientX - this.startX) < this.tapDistance
          && Math.abs(pointer.clientY - this.startY) < this.tapDistance) {
        recenter(pointer);
      }
      this.panning = false;
    },
    onMouseDown(event) {
      this.startX = event.clientX;
      this.startY = event.clientY;
      this.panning = event.button === 2 || event.ctrlKey || event.metaKey
        || event.shiftKey;
      if (!this.panning) return;

      this.lastX = this.startX;
      this.lastY = this.startY;
      this.startPan();
      event.stopPropagation();
    },
    onTouchStart(event) {
      this.startX = event.touches[0].clientX;
      this.startY = event.touches[0].clientY;
      this.panning = event.touches.length === 2;
      if (!this.panning) return;

      const { touches } = event;
      this.lastX = 0.5 * (touches[0].clientX + touches[1].clientX);
      this.lastY = 0.5 * (touches[0].clientY + touches[1].clientY);
      this.startPan();
    },
    onMouseMove(event) {
      if (!this.panning) return;

      this.movePan(event.clientX, event.clientY);
      event.stopPropagation();
    },
    onTouchMove(event) {
      if (!this.panning || event.touches.length !== 2) return;

      const { touches } = event;
      const thisX = 0.5 * (touches[0].clientX + touches[1].clientX);
      const thisY = 0.5 * (touches[0].clientY + touches[1].clientY);
      this.movePan(thisX, thisY);
    },
    onMouseUp(event) {
      this.recenter(event);
    },
    onTouchEnd(event) {
      if (event.touches.length === 0) {
        this.recenter(event.changedTouches[0]);
      }
    },
  },
};
</script>

<style>
model-viewer {
  padding: 0px;
  
  margin: 0px;
  height: 100%;
  width: 100%;
  outline: none;
}
.annotation {
  background-color: #888888;
  position: absolute;
  transform: translate(10px, 10px);
  border-radius: 10px;
  padding: 10px;
}
.ui {
  position: absolute;
  bottom: 0px;
  width: 120px;
}
.ui-button {
  position: absolute;
  bottom: 12px;
  align-items: center;
}
/* This keeps child nodes hidden while the element loads */
:not(:defined) > * {
  display: none;
}
#ar-button {
  display: none;
}
.logo {
  position: absolute;
  bottom: 24px;
  width: 60px;
}
</style>
